import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SyncIcon from '@mui/icons-material/Sync';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import Lottie from "lottie-react";
import confirmAsist from "../../lottie/confirmAsist.json";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonBasic } from '../../components';
const ModalLoadingSoftland = ({show, hideModal, timeout = 60, cuentaId = null}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(timeout);
    const [showSuccess, setShowSuccess] = useState(false);
    const handleClose = () => hideModal();
    const handleContinue = () => {
        if(showSuccess){
            if (cuentaId) {
                navigate(`/clientes/cuenta/${cuentaId}`);
            }
        }else{
            navigate('/tickets');
        }
        handleClose();
    };
    useEffect(() => {
        const countdownInterval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownInterval);
                    setShowSuccess(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            clearInterval(countdownInterval);
        };
    }, []);

    // Handle redirection when countdown finishes
    useEffect(() => {
        if (showSuccess && cuentaId) {
            const redirectTimeout = setTimeout(() => {
                navigate(`/contactos/${cuentaId}/cuenta`);
                handleClose();
            }, 2000);
            
            return () => clearTimeout(redirectTimeout);
        }
    }, [showSuccess, cuentaId]);

    return(
        <Dialog open={show} disableEscapeKeyDown maxWidth='sm' fullWidth sx={{backdropFilter: 'blur(4px);'}}>
            <Box sx={{
                background: isDarkMode ? 'linear-gradient(135deg, #2d3a52 0%, #1F2A40 100%)' : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                borderRadius: 2
            }}>
                <DialogContent sx={{ px: 4, py: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ maxWidth: 200, mx: 'auto', mb: 0 }}>
                                <Lottie animationData={confirmAsist} loop={!showSuccess} />
                            </Box>
                        </Grid>

                        {/* Success Message Card */}
                        <Grid item xs={12}>
                            <Box sx={{
                                background: isDarkMode ? 'rgba(134, 239, 172, 0.1)' : 'rgba(22, 163, 74, 0.08)',
                                border: `2px solid ${isDarkMode ? '#86efac' : '#16a34a'}`,
                                borderRadius: 2,
                                p: 2.5,
                                textAlign: 'center'
                            }}>
                                <Typography sx={{
                                    color: isDarkMode ? '#86efac' : '#16a34a', 
                                    fontWeight: 700, 
                                    fontSize: '1.3rem', 
                                    mb: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1
                                }}>
                                    <CheckCircleIcon sx={{ fontSize: '1.8rem' }} />
                                    Alta completada exitosamente
                                </Typography>
                                <Typography sx={{color: isDarkMode ? '#e0e0e0' : '#374151', fontSize: '0.9rem', mb: 2, fontWeight: 500}}>
                                    La cuenta se ha creado correctamente
                                </Typography>
                                {/* Sync Warning */}
                                <Box sx={{
                                    background: isDarkMode ? 'rgba(255, 167, 38, 0.15)' : 'rgba(245, 124, 0, 0.1)',
                                    borderLeft: `4px solid ${isDarkMode ? '#ffa726' : '#f57c00'}`,
                                    borderRadius: 1,
                                    p: 1,
                                    mb: 1
                                }}>
                                    <Typography sx={{color: isDarkMode ? '#ffa726' : '#f57c00', fontSize: '0.875rem', fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
                                        <FlashOnIcon sx={{ fontSize: '1.1rem' }} />
                                        Sincronizando cambios en el sistema
                                    </Typography>
                                    <Typography sx={{color: isDarkMode ? '#b0bec5' : '#6b7280', fontSize: '0.75rem'}}>
                                        Este proceso puede tomar aproximadamente 60 segundos
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Countdown Section */}
                        {!showSuccess ? (
                            <Grid item xs={12}>
                                <Box sx={{
                                    background: isDarkMode ? 'rgba(144, 202, 249, 0.1)' : 'rgba(3, 100, 255, 0.08)',
                                    borderRadius: 2,
                                    p: 1,
                                    textAlign: 'center',
                                    border: `1px solid ${isDarkMode ? '#3d4a62' : '#e5e7eb'}`
                                }}>
                                    <Typography sx={{color: isDarkMode ? '#90caf9' : '#0364ff', fontWeight: 700, fontSize: '2rem', lineHeight: 1}}>
                                        {countdown}
                                    </Typography>
                                    <Typography sx={{color: isDarkMode ? '#b0bec5' : '#6b7280', fontSize: '0.675rem', mt: 0.5, fontWeight: 500, letterSpacing: '0.5px'}}>
                                        SEGUNDOS RESTANTES
                                    </Typography>
                                </Box>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={{
                                    background: isDarkMode ? 'rgba(144, 202, 249, 0.1)' : 'rgba(3, 100, 255, 0.08)',
                                    borderRadius: 2,
                                    p: 1,
                                    textAlign: 'center',
                                    border: `1px solid ${isDarkMode ? '#3d4a62' : '#e5e7eb'}`
                                }}>
                                    {cuentaId && (
                                        <Typography sx={{color: isDarkMode ? '#90caf9' : '#0364ff', fontSize: '0.938rem', mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
                                            <SyncIcon sx={{ fontSize: '1.1rem' }} />
                                            Redirigiendo a la cuenta en 2 segundos...
                                        </Typography>
                                    )}
                                    <Typography sx={{color: isDarkMode ? '#b0bec5' : '#6b7280', fontSize: '0.813rem', fontStyle: 'italic'}}>
                                        Puede esperar o continuar con otra tarea
                                    </Typography>
                                </Box>
                            </Grid>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                <ButtonBasic
                                    textButton={showSuccess ? (cuentaId ? 'Ir a la cuenta' : 'Entendido') : 'Continuar con otra tarea'}
                                    onClick={handleContinue}
                                    variant='wk'
                                    color='nextStep'
                                    sx={{ minWidth: 240, py: 1.2, fontWeight: 600 }}
                                />
                                {!showSuccess && (
                                    <Typography sx={{
                                        color: isDarkMode ? '#90a4ae' : '#9ca3af', 
                                        fontSize: '0.75rem', 
                                        textAlign: 'center',
                                        maxWidth: '85%',
                                        lineHeight: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5
                                    }}>
                                        <LightbulbIcon sx={{ fontSize: '1rem' }} />
                                        La sincronización se está procesando. Puede cerrar este modal y continuar trabajando
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Box>
        </Dialog>
    )
}

export default ModalLoadingSoftland;